import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  get,
  getBookingSetup,
  type PrivateBookingSetup,
  type StudentList,
  type StudentSummary,
} from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { DateField } from "../calendar/DateField.js";
import {
  dayKey,
  formatDate,
  formatDay,
  formatFullDay,
  formatTime,
  isoDay,
} from "../format.js";
import { useAsync } from "../useAsync.js";
import type { BookingOffering } from "./api.js";
import {
  availabilityWindow,
  groupAvailabilitySlots,
  moveAvailabilityWindow,
  type AvailabilityRange,
} from "./availabilityRange.js";
import { bookingAttemptAfterFailure } from "./attempt.js";
import { defaultBookingOffering } from "./offeringDefault.js";
import { reportPeople, type ReportPerson } from "./people.js";
import { RecurringSeriesOutcome } from "./RecurringSeriesOutcome.js";
import {
  recurrenceFundingSummary,
  recurringLessons,
  type RecurrenceBookingResponse,
  type RecurrenceCadence,
} from "./recurrences.js";
import {
  applyRecurrenceFundingPreview,
  previewRecurringLessonOccurrences,
  recurringOutsideHoursWarning,
  type RecurrencePreview,
} from "./recurrencePreview.js";
import { staffBooking, type StaffAvailability } from "./staff.js";
import { staffWorkingHours } from "./staffWorkingHours.js";
import { tutorOneOffBooking } from "./tutorOneOffBooking.js";
import "./bookingControls.css";

interface CreateOptions {
  students: StudentSummary[];
  people: ReportPerson[];
}

interface BookingOfferingSnapshot {
  studentId: string;
  providerId: number | null;
  offerings: BookingOffering[];
}

/**
 * Student/tutor/service context already established by the ordinary staff
 * booking flow. These are authoritative ids from that flow, not names inferred
 * back into ids. The creator keeps the same recurrence state machine either
 * way; contextual mode keeps student/tutor fixed while seeding the lesson type
 * from the booking selection so it can still be changed directly in the
 * repeating flow.
 */
export interface RecurringLessonContext {
  studentId: string;
  studentName: string | null;
  providerId: number;
  providerName: string | null;
  serviceId: number;
  serviceName: string;
  /** Calendar hint only. Normal availability or an exact staff check must allow it. */
  initialStartsAt?: string | null;
}

async function loadCreateOptions(owner: boolean): Promise<CreateOptions> {
  const [studentList, people] = await Promise.all([
    get<StudentList>("/admin/students", {
      limit: "all",
      ...(owner ? {} : { scope: "mine" }),
    }),
    reportPeople(),
  ]);
  return {
    students: studentList.students,
    // A departed tutor is useful in a historic report but is never a sensible
    // target for a new recurring plan.
    people: people.filter((person) => !person.left),
  };
}

function providerName(
  people: readonly ReportPerson[],
  providerId: number | null,
): string {
  if (providerId === null) return "No tutor selected";
  return (
    people.find((person) => person.providerId === providerId)?.name ??
    `Tutor ${providerId}`
  );
}

function cadenceLabel(cadence: RecurrenceCadence): string {
  return cadence === "weekly" ? "every week" : "every two weeks";
}

function setupReady(setup: PrivateBookingSetup | null | undefined): boolean {
  return setup?.state === "linked";
}

function recurringOutcome(result: RecurrenceBookingResponse): ActionOutcome {
  return {
    variant: result.failed_count > 0 || result.unavailable_count > 0 || result.pending_count > 0 ? "attention" : "success",
    title: result.plan_created ? "Recurring lessons created" : "Recurring lessons confirmed",
    description: result.plan.student_name,
    details: [{ value: <RecurringSeriesOutcome result={result} /> }],
  };
}

/**
 * Create one FastAPI-owned recurrence plan. This component never decides how
 * many credits the series may spend. WMF-SIte owns funding policy: future
 * occurrences do not automatically consume credit, and the returned funding
 * state reflects the backend's current authoritative settlement state.
 *
 * With `context`, the booking flow fixes student/tutor and seeds the service
 * from the ordinary booking selection. The tutor can still change to another
 * service returned by that same student's authoritative booking-offerings
 * query without leaving Repeats. Without context, this is the existing
 * management-page creator. Both routes share everything from live first-start
 * availability through mandatory exact-time conflict preview and recurrence
 * creation.
 */
export function CreateRecurringLesson({
  onCreated,
  context = null,
}: {
  onCreated: () => void;
  context?: RecurringLessonContext | null;
}) {
  const publish = usePublishActionOutcome();
  const [hosted, setHosted] = useState(false);
  const { me } = useAuth();
  const owner = isOwner(me);
  const fixedProviderId =
    context?.providerId ?? (owner ? null : (me?.booking.providerId ?? null));
  const contextKey =
    context === null
      ? "management"
      : `${context.studentId}:${context.providerId}:${context.serviceId}:${context.initialStartsAt ?? ""}`;
  const options = useAsync(
    () =>
      context === null
        ? loadCreateOptions(owner)
        : Promise.resolve({ students: [], people: [] }),
    [owner, contextKey],
  );

  const [studentId, setStudentId] = useState(context?.studentId ?? "");
  const [providerId, setProviderId] = useState<number | null>(fixedProviderId);
  const [serviceId, setServiceId] = useState<number | null>(
    context?.serviceId ?? null,
  );
  const [preferredDate, setPreferredDate] = useState(() =>
    context?.initialStartsAt ? dayKey(context.initialStartsAt) : isoDay(0),
  );
  const [range, setRange] = useState<AvailabilityRange>(() =>
    context?.initialStartsAt ? "day" : "week",
  );
  const [slots, setSlots] = useState<StaffAvailability["slots"] | null>(null);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [initialStartOutsideHours, setInitialStartOutsideHours] = useState(false);
  const [cadence, setCadence] = useState<RecurrenceCadence>("weekly");
  const [occurrenceCountInput, setOccurrenceCountInput] = useState("4");
  const [continueIndefinitely, setContinueIndefinitely] = useState(false);
  const [notifyParticipants, setNotifyParticipants] = useState(false);
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState<RecurrencePreview | null>(null);
  const [busy, setBusy] = useState<
    "availability" | "preview" | "create" | null
  >(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<RecurrenceBookingResponse | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [attemptKey, setAttemptKey] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const pendingCalendarHint = useRef(context?.initialStartsAt ?? null);
  const autoDefaultedOfferingScope = useRef<string | null>(null);
  const firstStartOutsideHours = preview === null
    ? initialStartOutsideHours
    : preview.occurrences[0]?.warning === true;

  // Management mode: keep a non-owner's provider pinned to their own identity.
  // Contextual mode never runs this — student/tutor come from the booking host,
  // while the service remains editable within that fixed provider scope.
  useEffect(() => {
    if (context === null && !owner) setProviderId(fixedProviderId);
  }, [context, fixedProviderId, owner]);

  const bookingOfferings = useAsync(
    (): Promise<BookingOfferingSnapshot> => {
      const requestedStudentId = studentId;
      const requestedProviderId = providerId;
      if (requestedProviderId === null || requestedStudentId === "") {
        return Promise.resolve({
          studentId: requestedStudentId,
          providerId: requestedProviderId,
          offerings: [],
        });
      }

      return tutorOneOffBooking
        .offerings(requestedStudentId, requestedProviderId)
        .then((response) => ({
          studentId: requestedStudentId,
          providerId: requestedProviderId,
          // Recurring plans are allowed to start without current credit or a
          // one-off payment route. Funding is settled occurrence-by-occurrence
          // by the recurrence backend, so only tutor scope belongs here.
          offerings: response.offerings.filter(
            (offering) => offering.provider_id === requestedProviderId,
          ),
        }));
    },
    [contextKey, providerId, studentId],
  );
  const bookingSetup = useAsync(
    () =>
      owner && studentId !== ""
        ? getBookingSetup(studentId)
        : Promise.resolve(null),
    [owner, studentId],
  );

  // useAsync intentionally retains its last successful value while the next
  // request loads. Accept it only when it belongs to the current student/tutor
  // scope so switching students can never expose or apply the old default.
  const currentBookingOfferings =
    bookingOfferings.data?.studentId === studentId &&
    bookingOfferings.data.providerId === providerId
      ? bookingOfferings.data.offerings
      : null;
  const selectedStudent =
    options.data?.students.find((student) => student.id === studentId) ?? null;
  const selectedOffering =
    currentBookingOfferings?.find(
      (offering) => offering.service_id === serviceId,
    ) ?? null;
  const bookingReady = !owner || setupReady(bookingSetup.data);
  const people = options.data?.people ?? [];
  const studentDisplayName =
    context?.studentName ?? selectedStudent?.fullName ?? "Selected student";
  const tutorDisplayName =
    context?.providerName ?? providerName(people, providerId);
  const serviceDisplayName =
    selectedOffering?.service_name ?? context?.serviceName ?? "Selected service";
  const window = availabilityWindow(preferredDate, range);
  const today = isoDay(0);
  const parsedOccurrenceCount = Number(occurrenceCountInput);
  const occurrenceCount =
    occurrenceCountInput !== "" &&
    Number.isInteger(parsedOccurrenceCount) &&
    parsedOccurrenceCount >= 2 &&
    parsedOccurrenceCount <= 52
      ? parsedOccurrenceCount
      : null;

  // Apply the automatic staff default only once for each student/tutor scope.
  // That distinction matters: `null` after this effect has run can mean the
  // tutor explicitly cleared the dropdown, and an effect must not fight that
  // user action by immediately restoring a default.
  useEffect(() => {
    if (
      context !== null ||
      providerId === null ||
      studentId === "" ||
      currentBookingOfferings === null
    ) {
      return;
    }

    const scope = `${studentId}:${providerId}`;
    if (autoDefaultedOfferingScope.current === scope) return;
    autoDefaultedOfferingScope.current = scope;

    setServiceId((current) => {
      if (
        current !== null &&
        currentBookingOfferings.some(
          (offering) => offering.service_id === current,
        )
      ) {
        return current;
      }
      return defaultBookingOffering(currentBookingOfferings)?.service_id ?? null;
    });
  }, [context, currentBookingOfferings, providerId, studentId]);

  function changedIntent() {
    setAttemptKey(crypto.randomUUID());
    setProblem(null);
    setUncertain(false);
    setResult(null);
    setPreview(null);
  }

  function resetTimes() {
    pendingCalendarHint.current = null;
    setSlots(null);
    setStartsAt(null);
    setInitialStartOutsideHours(false);
    setPreview(null);
  }

  function chooseStudent(value: string) {
    if (value === studentId) return;
    setStudentId(value);
    setServiceId(null);
    autoDefaultedOfferingScope.current = null;
    resetTimes();
    changedIntent();
  }

  function chooseProvider(value: number | null) {
    if (value === providerId) return;
    setProviderId(value);
    setServiceId(null);
    autoDefaultedOfferingScope.current = null;
    resetTimes();
    changedIntent();
  }

  function chooseService(value: number | null) {
    if (value === serviceId) return;
    setServiceId(value);
    resetTimes();
    changedIntent();
  }

  function chooseDate(value: string) {
    pendingCalendarHint.current = null;
    setPreferredDate(value);
    resetTimes();
    changedIntent();
  }

  function changeRange(nextRange: AvailabilityRange) {
    if (nextRange === range) return;
    setRange(nextRange);
    resetTimes();
    changedIntent();
  }

  function moveWindow(direction: -1 | 1) {
    pendingCalendarHint.current = null;
    setPreferredDate((current) =>
      moveAvailabilityWindow(current, range, direction),
    );
    resetTimes();
    changedIntent();
  }

  async function findTimes() {
    if (
      providerId === null ||
      serviceId === null ||
      preferredDate === ""
    ) {
      return;
    }
    // Consume the initial Diary hint once. Only the current selection may be
    // retained for a same-context refresh; resetTimes clears both on changes.
    const hinted = pendingCalendarHint.current ?? startsAt;
    pendingCalendarHint.current = null;
    setBusy("availability");
    setProblem(null);
    setUncertain(false);
    setSlots(null);
    setStartsAt(null);
    setInitialStartOutsideHours(false);
    setPreview(null);
    try {
      const availability = await staffBooking.availability({
        provider_id: String(providerId),
        service_id: String(serviceId),
        start_date: window.startDate,
        end_date: window.endDate,
      });
      setSlots(availability.slots);

      if (hinted !== null) {
        if (availability.slots.some((slot) => slot.starts_at === hinted)) {
          setStartsAt(hinted);
        } else {
          // Public availability omits closed hours. Use the same staff-owned
          // policy as the occurrence preview before discarding a Diary hint.
          try {
            const exact = await staffWorkingHours.check({ providerId, serviceId, startsAt: hinted });
            const outsideHours = recurringOutsideHoursWarning(exact);
            if (exact.state === "available" || outsideHours) {
              setStartsAt(hinted);
              setInitialStartOutsideHours(outsideHours);
            } else {
              setProblem(
                "The time you clicked is not available for recurring lessons. Nothing was booked; choose one of the current times below.",
              );
            }
          } catch {
            pendingCalendarHint.current = hinted;
            setProblem(
              `We loaded the available times, but could not check your selected Diary time. Nothing was booked. Choose Show this ${range} to try again, or select another available time.`,
            );
          }
        }
      }
    } catch (cause) {
      pendingCalendarHint.current = hinted;
      setProblem(
        cause instanceof Error
          ? cause.message
          : "Could not load available times.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function runPreview(): Promise<RecurrencePreview | null> {
    if (
      providerId === null ||
      serviceId === null ||
      startsAt === null ||
      occurrenceCount === null
    ) {
      return null;
    }
    if (bookingOfferings.loading || bookingOfferings.error) {
      setPreview(null);
      setProblem(
        "Current booking and funding eligibility could not be confirmed. Nothing has been booked.",
      );
      return null;
    }
    setBusy("preview");
    setInitialStartOutsideHours(false);
    setPreview(null);
    setProblem(null);
    setUncertain(false);
    try {
      const checked = await previewRecurringLessonOccurrences({
        providerId,
        serviceId,
        startsAt,
        cadence,
        occurrenceCount,
      });
      const withFunding = applyRecurrenceFundingPreview(checked, selectedOffering);
      setPreview(withFunding);
      return withFunding;
    } catch (cause) {
      setPreview(null);
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The recurring lesson preview could not be checked.",
      );
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      studentId === "" ||
      providerId === null ||
      serviceId === null ||
      startsAt === null ||
      occurrenceCount === null ||
      !bookingReady ||
      preview === null ||
      preview.conflictCount > 0 ||
      preview.occurrences.some(
        (occurrence) =>
          occurrence.preflight === null ||
          occurrence.preflight.status === "refused",
      )
    ) {
      return;
    }

    // Recheck immediately before the write. A conflict can appear after the
    // visible preview; in that case no recurrence request is sent and the tutor
    // sees the fresh conflict list instead. Outside-hours warnings remain
    // non-blocking, matching the preflight shown to the tutor.
    setBusy("preview");
    setInitialStartOutsideHours(false);
    setPreview(null);
    setProblem(null);
    setUncertain(false);
    let currentPreview: RecurrencePreview;
    try {
      tutorOneOffBooking.invalidateOfferings(studentId);
      const [availabilityPreview, freshOfferings] = await Promise.all([
        previewRecurringLessonOccurrences({
          providerId,
          serviceId,
          startsAt,
          cadence,
          occurrenceCount,
        }),
        tutorOneOffBooking.offerings(studentId, providerId),
      ]);
      const freshOffering =
        freshOfferings.offerings.find(
          (offering) =>
            offering.provider_id === providerId &&
            offering.service_id === serviceId,
        ) ?? null;
      currentPreview = applyRecurrenceFundingPreview(
        availabilityPreview,
        freshOffering,
      );
      setPreview(currentPreview);
    } catch (cause) {
      setBusy(null);
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The recurring lesson preview could not be rechecked.",
      );
      return;
    }

    const refusedCount = currentPreview.occurrences.filter(
      (occurrence) => occurrence.preflight?.status === "refused",
    ).length;
    if (currentPreview.conflictCount > 0 || refusedCount > 0) {
      setBusy(null);
      setProblem(
        "Availability or booking eligibility changed since the last preview. Resolve the results below before booking the series.",
      );
      return;
    }

    setBusy("create");
    try {
      const created = await recurringLessons.create({
        portal_student_id: studentId,
        provider_id: providerId,
        service_id: serviceId,
        funding_mode: "credit_then_pending",
        starts_at: startsAt,
        cadence,
        occurrence_count: occurrenceCount,
        continue_indefinitely: continueIndefinitely,
        persons: 1,
        appointment_status: "approved",
        internal_notes: notes.trim(),
        notify_participants: notifyParticipants,
        idempotency_key: attemptKey,
      });
      setResult(created);
      setHosted(publish(recurringOutcome(created)));
      onCreated();
    } catch (cause) {
      const attempt = bookingAttemptAfterFailure(
        attemptKey,
        cause,
        () => crypto.randomUUID(),
      );
      setAttemptKey(attempt.key);
      setUncertain(attempt.kind === "uncertain");
      if (attempt.kind === "uncertain") setHosted(publish({ variant: "uncertain", title: "Recurring booking outcome needs checking", description: context?.studentName ?? "Recurring lessons", details: [{ value: "The series outcome is uncertain. Check lessons and credits before retrying. Do not change the details yet; retrying this exact series will use the same attempt key so the booking service can replay it safely." }] }));
      setProblem(
        attempt.kind === "uncertain"
          ? "The series outcome is uncertain. Check lessons and credits before retrying. Do not change the details yet; retrying this exact series will use the same attempt key so the booking service can replay it safely."
          : cause instanceof Error
            ? cause.message
            : "Could not create the recurring lesson.",
      );
    } finally {
      setBusy(null);
    }
  }

  if (options.loading && !options.data) {
    return <p className="muted">Loading recurring lesson options…</p>;
  }

  if (options.error && !options.data) {
    return <div className="notice error">{options.error}</div>;
  }

  const recordPath = owner
    ? `/students/${studentId}`
    : `/my-students/${studentId}`;
  const refusedCount =
    preview?.occurrences.filter(
      (occurrence) => occurrence.preflight?.status === "refused",
    ).length ?? 0;
  const availableCount =
    preview?.occurrences.filter(
      (occurrence) => occurrence.preflight?.status === "available",
    ).length ?? 0;
  const noCreditCount =
    preview?.occurrences.filter(
      (occurrence) => occurrence.preflight?.status === "no_credit",
    ).length ?? 0;
  const canPreview =
    studentId !== "" &&
    providerId !== null &&
    serviceId !== null &&
    startsAt !== null &&
    occurrenceCount !== null &&
    bookingReady &&
    currentBookingOfferings !== null &&
    !bookingOfferings.loading &&
    !bookingOfferings.error &&
    busy === null;
  const canSubmit =
    canPreview &&
    preview !== null &&
    preview.conflictCount === 0 &&
    refusedCount === 0 &&
    preview.occurrences.every((occurrence) => occurrence.preflight !== null);

  return (
    <section
      className="panel"
      data-recurring-lesson-creator={
        context === null ? "management" : "booking"
      }
    >
      <h2 style={{ marginTop: 0 }}>Book recurring lessons</h2>
      <p className="small muted">
        Choose how often the lessons repeat, then preview every date before booking.
      </p>

      <form onSubmit={(event) => void submit(event)}>
        {context === null ? (
          <div
            className="row"
            style={{ alignItems: "flex-end", flexWrap: "wrap" }}
          >
            <label className="stack" style={{ minWidth: 220, flex: 1 }}>
              <span className="small muted">Student</span>
              <select
                value={studentId}
                disabled={busy !== null}
                onChange={(event) => chooseStudent(event.target.value)}
              >
                <option value="">Choose a student</option>
                {(options.data?.students ?? []).map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.fullName}
                  </option>
                ))}
              </select>
            </label>

            {owner ? (
              <label className="stack" style={{ minWidth: 200, flex: 1 }}>
                <span className="small muted">Tutor</span>
                <select
                  value={providerId ?? ""}
                  disabled={busy !== null}
                  onChange={(event) =>
                    chooseProvider(
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                    )
                  }
                >
                  <option value="">Choose a tutor</option>
                  {people.map((person) => (
                    <option key={person.providerId} value={person.providerId}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="stack" style={{ minWidth: 200, flex: 1 }}>
                <span className="small muted">Tutor</span>
                <strong>{providerName(people, providerId)}</strong>
              </div>
            )}

            <label className="stack" style={{ minWidth: 220, flex: 1 }}>
              <span className="small muted">Lesson type</span>
              <select
                aria-label="Lesson type"
                value={serviceId ?? ""}
                disabled={
                  busy !== null ||
                  providerId === null ||
                  studentId === "" ||
                  bookingOfferings.loading
                }
                onChange={(event) =>
                  chooseService(
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  )
                }
              >
                <option value="">
                  {bookingOfferings.loading
                    ? "Loading lesson types…"
                    : "Choose a lesson type"}
                </option>
                {(currentBookingOfferings ?? []).map((offering) => (
                  <option
                    key={`${offering.provider_id}:${offering.service_id}`}
                    value={offering.service_id}
                  >
                    {offering.service_name ??
                      `Lesson service ${offering.service_id}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <div className="notice">
            <strong>{studentDisplayName}</strong>
            <p className="small muted" style={{ marginBottom: 10 }}>
              Tutor: {tutorDisplayName}
            </p>
            <label className="stack">
              <span className="small muted">Lesson type</span>
              <select
                aria-label="Recurring lesson type"
                value={serviceId ?? ""}
                disabled={
                  busy !== null ||
                  providerId === null ||
                  bookingOfferings.loading
                }
                onChange={(event) =>
                  chooseService(
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  )
                }
              >
                {!currentBookingOfferings?.some(
                  (offering) => offering.service_id === context.serviceId,
                ) && (
                  <option value={context.serviceId}>
                    {context.serviceName}
                  </option>
                )}
                {(currentBookingOfferings ?? []).map((offering) => (
                  <option
                    key={`${offering.provider_id}:${offering.service_id}`}
                    value={offering.service_id}
                  >
                    {offering.service_name ??
                      `Lesson service ${offering.service_id}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {bookingOfferings.error && (
          <div className="error small" style={{ marginTop: 10 }}>
            {bookingOfferings.error}
          </div>
        )}

        {owner && studentId !== "" && bookingSetup.loading && (
          <p className="small muted">Checking private booking setup…</p>
        )}
        {owner && studentId !== "" && bookingSetup.error && (
          <div className="error small">{bookingSetup.error}</div>
        )}
        {owner &&
          studentId !== "" &&
          bookingSetup.data &&
          !setupReady(bookingSetup.data) && (
            <div className="notice" style={{ marginTop: 12 }}>
              <strong>Private booking is not ready for this student.</strong>
              <p className="small" style={{ marginBottom: 0 }}>
                A series can fall through to payment-pending occurrences, so it
                needs a booking payer on file even when a single lesson would
                not. Complete the Booking payer setup on the student record
                first. <Link to={recordPath}>Open student record</Link>
              </p>
            </div>
          )}

        <div className="availability-controls booking-availability-controls">
          <div
            className="row segmented-control"
            role="group"
            aria-label="First lesson availability range"
          >
            <button
              aria-pressed={range === "day"}
              className={range === "day" ? "action-primary" : "action-tertiary"}
              disabled={busy !== null}
              onClick={() => changeRange("day")}
              type="button"
            >
              Day
            </button>
            <button
              aria-pressed={range === "week"}
              className={
                range === "week" ? "action-primary" : "action-tertiary"
              }
              disabled={busy !== null}
              onClick={() => changeRange("week")}
              type="button"
            >
              Week
            </button>
          </div>
          <div className="row">
            <button
              aria-label={`Previous ${range}`}
              className="action-tertiary"
              disabled={busy !== null || preferredDate <= today}
              onClick={() => moveWindow(-1)}
              type="button"
            >
              ← Previous
            </button>
            <strong className="availability-window">
              {range === "day"
                ? formatFullDay(window.startDate)
                : `${formatFullDay(window.startDate)} – ${formatFullDay(
                    moveAvailabilityWindow(window.endDate, "day", -1),
                  )}`}
            </strong>
            <button
              aria-label={`Next ${range}`}
              className="action-tertiary"
              disabled={busy !== null}
              onClick={() => moveWindow(1)}
              type="button"
            >
              Next →
            </button>
          </div>
        </div>

        <div
          className="row"
          data-booking-date-controls
          style={{ alignItems: "flex-end", marginTop: 10, flexWrap: "wrap" }}
        >
          <DateField
            label="Jump to date"
            value={preferredDate}
            disabled={busy !== null}
            onChange={chooseDate}
          />
          <button
            type="button"
            className="action-secondary"
            disabled={
              busy !== null ||
              providerId === null ||
              serviceId === null ||
              preferredDate === ""
            }
            onClick={() => void findTimes()}
          >
            {busy === "availability" ? "Finding times…" : `Show this ${range}`}
          </button>
        </div>

        {slots !== null && slots.length === 0 && startsAt === null && (
          <p className="empty">
            No available times in this {range}. Try another {range}.
          </p>
        )}
        {slots !== null && slots.length > 0 && (
          <div className="availability-days" style={{ marginTop: 12 }}>
            {groupAvailabilitySlots(slots).map(([date, daySlots]) => (
              <section className="availability-day" key={date}>
                <h3 className="day-heading">
                  {formatDay(daySlots[0]!.starts_at)}
                </h3>
                <div className="slot-grid">
                  {daySlots.map((slot) => (
                    <button
                      key={slot.starts_at}
                      type="button"
                      className={
                        startsAt === slot.starts_at
                          ? "action-primary"
                          : "action-tertiary"
                      }
                      disabled={busy !== null}
                      onClick={() => {
                        if (startsAt === slot.starts_at) return;
                        pendingCalendarHint.current = null;
                        changedIntent();
                        setStartsAt(slot.starts_at);
                        setInitialStartOutsideHours(false);
                      }}
                    >
                      {formatTime(slot.starts_at)}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div
          className="row"
          style={{ alignItems: "flex-end", marginTop: 14, flexWrap: "wrap" }}
        >
          <label className="stack">
            <span className="small muted">Repeat</span>
            <select
              value={cadence}
              disabled={busy !== null}
              onChange={(event) => {
                setCadence(event.target.value as RecurrenceCadence);
                changedIntent();
              }}
            >
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Every two weeks</option>
            </select>
          </label>
          <label className="stack">
            <span className="small muted">Lessons to create now</span>
            <input
              type="number"
              min={2}
              max={52}
              step={1}
              value={occurrenceCountInput}
              disabled={busy !== null}
              onChange={(event) => {
                setOccurrenceCountInput(event.target.value);
                changedIntent();
              }}
            />
            <span className="small muted">
              Includes the first lesson selected above.
            </span>
          </label>
          <label className="booking-checkbox small">
            <input
              type="checkbox"
              checked={continueIndefinitely}
              disabled={busy !== null}
              onChange={(event) => {
                setContinueIndefinitely(event.target.checked);
                changedIntent();
              }}
            />
            <span>Keep the plan running</span>
          </label>
          <label className="booking-checkbox small">
            <input
              type="checkbox"
              checked={notifyParticipants}
              disabled={busy !== null}
              onChange={(event) => {
                setNotifyParticipants(event.target.checked);
                changedIntent();
              }}
            />
            <span>Send booking notifications</span>
          </label>
        </div>

        <label className="stack" style={{ marginTop: 14 }}>
          <span className="small muted">Internal notes (optional)</span>
          <textarea
            rows={2}
            maxLength={2000}
            value={notes}
            disabled={busy !== null}
            onChange={(event) => {
              setNotes(event.target.value);
              changedIntent();
            }}
          />
        </label>

        {studentId !== "" &&
          serviceId !== null &&
          startsAt &&
          providerId !== null && (
            <div className="notice" style={{ marginTop: 14 }}>
              <strong>Series summary</strong>
              <p style={{ marginBottom: 4 }}>
                Book {serviceDisplayName} for {studentDisplayName} with{" "}
                {tutorDisplayName} {cadenceLabel(cadence)}, starting{" "}
                {formatDate(startsAt)} at {formatTime(startsAt)}.
              </p>
              <p className="small muted" style={{ margin: 0 }}>
                {continueIndefinitely
                  ? `Create ${occurrenceCountInput || "—"} lessons now, including the first lesson, then continue adding future lessons as the plan runs.`
                  : `Create ${occurrenceCountInput || "—"} lessons in total, including the first lesson selected above.`}
                {notifyParticipants
                  ? " Booking notifications are on."
                  : " Booking notifications are off."}
              </p>
              {firstStartOutsideHours && (
                <p className="small" role="status">
                  The first lesson is outside normal teaching hours. Review the preview before creating the series.
                </p>
              )}
            </div>
          )}

        {bookingOfferings.loading && currentBookingOfferings === null && (
          <p className="small muted">
            Checking current booking and funding eligibility…
          </p>
        )}
        {bookingOfferings.error && (
          <div className="notice error" role="alert">
            Current booking and funding eligibility could not be confirmed:{" "}
            {bookingOfferings.error}
          </div>
        )}
        {problem && (
          <>{uncertain ? !hosted && <ActionConfirmation variant="uncertain" title="Recurring booking outcome needs checking" description={problem} reveal /> : <div className="notice error" role="alert">{problem}</div>}</>
        )}

        <button
          type="button"
          className="action-secondary"
          disabled={!canPreview}
          style={{ marginTop: 14 }}
          onClick={() => void runPreview()}
        >
          {busy === "preview"
            ? "Checking every lesson…"
            : "Preview recurring lessons"}
        </button>

        {preview && (
          <div
            className={
              preview.conflictCount > 0 || refusedCount > 0
                ? "notice error"
                : "notice"
            }
            style={{ marginTop: 14 }}
            role="status"
          >
            <strong>
              {availableCount} valid · {preview.conflictCount} unavailable ·{" "}
              {noCreditCount} no credit · {refusedCount} refused
            </strong>
            <p className="small" style={{ marginBottom: 8 }}>
              This preview does not reserve credits. The booking system checks
              each lesson again when you create the series. Resolve unavailable
              dates and payment issues before continuing. Lessons with payment
              still due are listed below.
              {preview.warningCount > 0
                ? " Outside-hours warnings do not block the series."
                : ""}
            </p>
            <ul className="small" style={{ marginBottom: 0 }}>
              {preview.occurrences.map((occurrence) => {
                const decision = occurrence.preflight;
                const detail =
                  decision?.status === "available"
                    ? "Valid — credit available at preview time"
                    : decision?.status === "no_credit"
                      ? decision.message
                      : decision?.status === "unavailable"
                        ? `Unavailable: ${decision.message}`
                        : decision?.status === "refused"
                          ? `Refused: ${decision.message}`
                          : "Funding decision not confirmed";
                return (
                  <li key={occurrence.startsAt}>
                    {formatDate(occurrence.startsAt)} at{" "}
                    {formatTime(occurrence.startsAt)} — {detail}
                    {occurrence.warning
                      ? " · Outside normal teaching hours"
                      : ""}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!preview && canPreview && (
          <p className="small muted">
            Preview is required before the recurring lessons can be created.
          </p>
        )}

        <button type="submit" disabled={!canSubmit} style={{ marginTop: 14 }}>
          {busy === "create"
            ? "Creating recurring lessons…"
            : "Create recurring lessons"}
        </button>
      </form>

      {result && !hosted && <ActionConfirmation {...recurringOutcome(result)} reveal />}

    </section>
  );
}
